import {ChangeDetectionStrategy, Component, computed, input, signal} from "@angular/core";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {MøtedeltakerId, ProtokollførtDeltakerstatus, ProtokollførtMøtedeltakerModell, ProtokollførtMøtedeltakerSpesifikasjon} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {MatIcon} from "@angular/material/icon";
import {TranslateModule} from "@ngx-translate/core";
import {MatError, MatFormField} from "@angular/material/form-field";
import {MatInput} from "@angular/material/input";
import {StandardValideringsfeilPipe} from "felles-ng2/pipes/StandardValideringsfeilPipe";
import {MatOption, MatSelect} from "@angular/material/select";
import {MatSlideToggle} from "@angular/material/slide-toggle";
import {FeltValidatorer} from "felles-ng2/infrastruktur/angular/feltValidatorer";
import {primærRolleSomStreng} from "felles-ng2/modell/medlemsRollePeriodeModellHjelpere";
import {FormaterProsentPipe} from "felles-ng2/pipes/FormaterProsentPipe";
import {MatTooltip} from "@angular/material/tooltip";

const debug = initDebugger('MøteprotokollRedigerDeltakerKomponent');

export type SkjemaType = ReturnType<typeof MøteprotokollRedigerDeltakerKomponent.opprettSkjema>;
export type SkjemaVerdier = SkjemaType['value'];

/**
 * Skjem for redigering av protokollført møtedeltaker
 */
@Component({
    selector: 'moeteprotokoll-rediger-deltaker',
    templateUrl: 'møteprotokoll-rediger-deltaker.html',
    styleUrl: 'møteprotokoll-rediger-deltaker.scss',
    imports: [
        MatIcon,
        TranslateModule,
        MatFormField,
        MatInput,
        ReactiveFormsModule,
        MatError,
        StandardValideringsfeilPipe,
        MatSelect,
        MatOption,
        MatSlideToggle,
        FormaterProsentPipe,
        MatTooltip
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MøteprotokollRedigerDeltakerKomponent {

    static valideringsfeil = {skjermet_kan_ikke_signere: 'skjermet_kan_ikke_signere'}

    static opprettSkjema(deltaker: ProtokollførtMøtedeltakerModell) {
        return new FormGroup({
            medlemId: new FormControl<MøtedeltakerId>(deltaker.medlem.id, {nonNullable: true, validators: Validators.required}),
            status: new FormControl<ProtokollførtDeltakerstatus>(deltaker.status, {nonNullable: true, validators: Validators.required}),
            kommentar: new FormControl(deltaker.kommentar, {nonNullable: false, validators: FeltValidatorer.litenStreng}),
            skalSignere: new FormControl(deltaker.skalSignere, {nonNullable: true}),
            deltaker: new FormControl<ProtokollførtMøtedeltakerModell>(deltaker, {nonNullable: true})
        });
    }

    static somSpesifikasjon(skjema: SkjemaVerdier): ProtokollførtMøtedeltakerSpesifikasjon {
        return {
            medlemId: skjema.medlemId!,
            kommentar: skjema.kommentar ?? null,
            status: skjema.status!,
            skalSignere: skjema.skalSignere!
        };
    }

    skjema = input.required<SkjemaType>();
    visRepresentererKolonne = input(false);

    aktivRolle = computed(() => {
        return primærRolleSomStreng(this.skjema().value.deltaker!.aktiveMedlemsroller);
    })
    sumAndelIProsent = computed(() => this.skjema().value.deltaker!.aksjeeierRepresentasjoner.reduce((sum, aksjeeier) => sum + aksjeeier.eierandel, 0));
    viserRepresentasjoner = signal(false);
    statuser = ProtokollførtDeltakerstatus;
    valideringsfeil = MøteprotokollRedigerDeltakerKomponent.valideringsfeil;

    constructor() {
    }

    toggleRepresentasjoner() {
        this.viserRepresentasjoner.update(viser => !viser);
    }
}
